import type { Tab } from './index';
import { esc } from '../utils';
import { getWorkerBase } from '../worker';

interface OptionContract {
  strike:           number;
  lastPrice?:       number;
  bid?:             number;
  ask?:             number;
  volume?:          number;
  openInterest?:    number;
  impliedVolatility?:number;
  delta?:           number;
  gamma?:           number;
  theta?:           number;
}

export class OptionsChainTab implements Tab {
  readonly id = 'optionschain';

  private ticker: string | null = null;
  private stockPrice = 0;
  private allData: Record<number, { calls: OptionContract[]; puts: OptionContract[] }> = {};
  private chainView: 'both' | 'calls' | 'puts' = 'both';

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    ['oc-load-btn','oc-load-ticker-btn'].forEach(id =>
      this.el(id)?.addEventListener('click', () =>
        this.load((this.el('oc-ticker-input') as HTMLInputElement).value.trim().toUpperCase()),
      ),
    );
    (this.el('oc-ticker-input') as HTMLInputElement).addEventListener('keydown', e => {
      if (e.key === 'Enter') this.load((e.target as HTMLInputElement).value.trim().toUpperCase());
    });

    document.querySelectorAll<HTMLElement>('[data-oc-ticker]').forEach(btn =>
      btn.addEventListener('click', () => this.load(btn.dataset.ocTicker ?? '')),
    );

    (this.el('oc-expiry-select') as HTMLSelectElement).addEventListener('change', e =>
      this.renderExpiry(Number((e.target as HTMLSelectElement).value)),
    );

    document.querySelectorAll<HTMLElement>('[data-oc-view]').forEach(btn =>
      btn.addEventListener('click', () => this.setView(btn.dataset.ocView as 'both' | 'calls' | 'puts')),
    );

    (this.el('oc-itm-only') as HTMLInputElement).addEventListener('change', () => this.applyFilters());
    (this.el('oc-high-vol') as HTMLInputElement).addEventListener('change', () => this.applyFilters());

    // Expose for cross-tab calls (Holdings tab stub wires to this)
    (window as any).loadOptionsFromTracker = (ticker: string) => {
      (window as any).showTab?.('optionschain');
      setTimeout(() => this.load(ticker), 100);
    };
  }

  render(): void { /* user-driven — no auto-load */ }

  private ocUrl(ticker: string, date?: number): string {
    const base = getWorkerBase();
    return base + `/api/options-chain?ticker=${encodeURIComponent(ticker)}` + (date ? `&date=${date}` : '');
  }

  async load(ticker: string): Promise<void> {
    if (!ticker) return;
    this.ticker = ticker;
    (this.el('oc-ticker-input') as HTMLInputElement).value = ticker;

    this.el('oc-loading').style.display       = 'block';
    this.el('oc-empty').style.display         = 'none';
    this.el('oc-error').style.display         = 'none';
    this.el('oc-chain-container').style.display = 'none';

    try {
      const res    = await fetch(this.ocUrl(ticker), { signal: AbortSignal.timeout(15000) });
      const parsed = await res.json();
      const result = parsed?.optionChain?.result?.[0];
      if (!result) throw new Error(`No options data for ${ticker}`);

      const quote = result.quote;
      this.stockPrice = quote?.regularMarketPrice ?? 0;
      const prev      = quote?.regularMarketPreviousClose ?? this.stockPrice;
      const change    = this.stockPrice - prev;
      const changePct = prev ? (change / prev * 100) : 0;

      this.el('oc-stat-price').textContent = `$${this.stockPrice.toFixed(2)}`;
      (this.el('oc-stat-price') as HTMLElement).style.color = 'var(--accent)';

      const expiryDates: number[] = result.expirationDates ?? [];
      const select = this.el('oc-expiry-select') as HTMLSelectElement;
      select.innerHTML = expiryDates.map(ts => {
        const d = new Date(ts * 1000);
        const label = d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
        const days  = Math.round((d.getTime() - Date.now()) / 86400000);
        return `<option value="${ts}">${label} (${days}d)</option>`;
      }).join('');

      this.allData = {};
      if (result.options?.[0] && expiryDates[0]) {
        this.allData[expiryDates[0]] = result.options[0];
      }

      this.el('oc-loading').style.display = 'none';
      this.el('oc-last-updated').textContent = `${ticker} · Updated ${new Date().toLocaleTimeString()}`;
      this.el('oc-stock-price').textContent  = `$${this.stockPrice.toFixed(2)}`;
      this.el('oc-stock-change').innerHTML   = `<span style="color:${change>=0?'var(--green)':'var(--red)'};">${change>=0?'+':''}${change.toFixed(2)} (${changePct.toFixed(2)}%)</span>`;

      if (expiryDates.length) {
          await this.fetchAndRenderExpiry(ticker, expiryDates[0]);
      }
    } catch (err: unknown) {
      this.el('oc-loading').style.display = 'none';
      this.showError(`Could not load options for ${ticker}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  private async fetchAndRenderExpiry(ticker: string, ts: number): Promise<void> {
    if (this.allData[ts]) { this.renderExpiry(ts); return; }
    this.el('oc-loading').style.display       = 'block';
    this.el('oc-chain-container').style.display = 'none';
    try {
      const res    = await fetch(this.ocUrl(ticker, ts), { signal: AbortSignal.timeout(15000) });
      const parsed = await res.json();
      const result = parsed?.optionChain?.result?.[0];
      if (!result?.options?.[0]) throw new Error('No data for this expiry');
      this.allData[ts] = result.options[0];
      this.el('oc-loading').style.display = 'none';
      this.renderExpiry(ts);
    } catch (err: unknown) {
      this.el('oc-loading').style.display = 'none';
      this.showError(`Could not load this expiry: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  private renderExpiry(ts: number): void {
    if (!this.allData[ts]) { this.fetchAndRenderExpiry(this.ticker!, ts); return; }
    const data       = this.allData[ts];
    const calls      = data.calls ?? [];
    const puts       = data.puts  ?? [];
    const stockPrice = this.stockPrice;

    const totalCallOI = calls.reduce((s, c) => s + (c.openInterest ?? 0), 0);
    const totalPutOI  = puts.reduce((s, c)  => s + (c.openInterest ?? 0), 0);
    const pcr = totalCallOI > 0 ? (totalPutOI / totalCallOI).toFixed(2) : '—';

    const allStrikes = [...new Set([...calls, ...puts].map(o => o.strike))].sort((a,b) => a-b);
    let maxPainStrike = 0, minPain = Infinity;
    allStrikes.forEach(strike => {
      const pain = calls.reduce((s,c) => s + (c.openInterest??0)*Math.max(0,strike-c.strike), 0)
                 + puts.reduce((s,p)  => s + (p.openInterest??0)*Math.max(0,p.strike-strike), 0);
      if (pain < minPain) { minPain = pain; maxPainStrike = strike; }
    });

    const maxCallOI = calls.reduce((a,b) => (b.openInterest??0) > (a.openInterest??0) ? b : a, {} as OptionContract);
    const maxPutOI  = puts.reduce ((a,b) => (b.openInterest??0) > (a.openInterest??0) ? b : a, {} as OptionContract);
    const atmCall   = calls.reduce((a,b) => Math.abs(b.strike-stockPrice) < Math.abs(a.strike-stockPrice) ? b : a, calls[0] ?? {} as OptionContract);
    const atmPut    = puts.reduce ((a,b) => Math.abs(b.strike-stockPrice) < Math.abs(a.strike-stockPrice) ? b : a, puts[0]  ?? {} as OptionContract);
    const impliedMove = (atmCall?.lastPrice && atmPut?.lastPrice)
      ? `±${(((atmCall.lastPrice + atmPut.lastPrice) / stockPrice) * 100).toFixed(1)}%` : '—';

    const setPcr = this.el('oc-stat-pcr');
    setPcr.textContent = pcr;
    const pcrNum = parseFloat(pcr);
    setPcr.style.color = pcrNum > 1 ? 'var(--red)' : pcrNum < 0.7 ? 'var(--green)' : 'var(--warn)';
    this.el('oc-stat-maxpain').textContent = `$${maxPainStrike?.toFixed(2)}`;
    this.el('oc-stat-calloi').textContent  = maxCallOI.strike ? `$${maxCallOI.strike} (${(maxCallOI.openInterest??0).toLocaleString()})` : '—';
    this.el('oc-stat-putoi').textContent   = maxPutOI.strike  ? `$${maxPutOI.strike}  (${(maxPutOI.openInterest??0).toLocaleString()})` : '—';
    this.el('oc-stat-move').textContent    = impliedMove;

    this.renderTable('calls', calls, stockPrice, maxPainStrike);
    this.renderTable('puts',  puts,  stockPrice, maxPainStrike);

    this.el('oc-chain-container').style.display = 'block';
    this.setView(this.chainView);
    this.applyFilters();
  }

  private renderTable(type: 'calls' | 'puts', contracts: OptionContract[], stockPrice: number, maxPain: number): void {
    const tbody  = this.el(`oc-${type}-body`);
    const isCall = type === 'calls';
    const sorted = [...contracts].sort((a,b) => a.strike - b.strike);

    tbody.innerHTML = sorted.map(c => {
      const strike   = c.strike ?? 0;
      const isITM    = isCall ? strike < stockPrice : strike > stockPrice;
      const isATM    = Math.abs(strike - stockPrice) < stockPrice * 0.005;
      const isMaxPain= Math.abs(strike - maxPain) < 0.01;
      const highVol  = (c.volume ?? 0) > 100;

      const iv        = c.impliedVolatility ? `${(c.impliedVolatility*100).toFixed(1)}%` : '—';
      const delta     = c.delta != null ? c.delta.toFixed(3) : isCall ? (isITM ? '~0.70' : '~0.30') : (isITM ? '~-0.70' : '~-0.30');
      const intrinsic = (isCall ? Math.max(0, stockPrice - strike) : Math.max(0, strike - stockPrice)).toFixed(2);
      const volColor  = (c.volume??0) > 1000 ? 'var(--warn)' : (c.volume??0) > 100 ? 'var(--text)' : 'var(--text3)';
      const oiColor   = (c.openInterest??0) > 5000 ? 'var(--accent)' : (c.openInterest??0) > 500 ? 'var(--text)' : 'var(--text3)';

      let rowBg = '';
      if (isATM)      rowBg = 'background:rgba(0,212,255,0.08);border-left:3px solid var(--accent);';
      else if (isITM) rowBg = 'background:rgba(46,213,115,0.04);';

      const badges = [
        isATM                   ? `<span style="background:rgba(0,212,255,0.15);color:var(--accent);padding:1px 5px;border-radius:2px;font-size:9px;">ATM</span>` : '',
        isITM && !isATM         ? `<span style="background:rgba(46,213,115,0.12);color:var(--green);padding:1px 5px;border-radius:2px;font-size:9px;">ITM</span>` : '',
        !isITM && !isATM        ? `<span style="background:rgba(122,157,181,0.1);color:var(--text3);padding:1px 5px;border-radius:2px;font-size:9px;">OTM</span>` : '',
        highVol                 ? `<span style="background:rgba(255,184,48,0.12);color:var(--warn);padding:1px 5px;border-radius:2px;font-size:9px;">🔥 VOL</span>` : '',
        isMaxPain               ? `<span style="background:rgba(244,114,182,0.12);color:#f472b6;padding:1px 5px;border-radius:2px;font-size:9px;">MAX PAIN</span>` : '',
      ].filter(Boolean).join(' ');

      return `<tr style="${rowBg}" class="oc-row" data-itm="${isITM}" data-vol="${c.volume??0}" data-strike="${strike}">
        <td><strong style="color:${isCall?'var(--green)':'var(--red)'};">$${strike.toFixed(2)}</strong></td>
        <td>$${(c.lastPrice??0).toFixed(2)}</td>
        <td style="color:var(--text2);">${c.bid  != null ? '$'+c.bid.toFixed(2)  : '—'}</td>
        <td style="color:var(--text2);">${c.ask  != null ? '$'+c.ask.toFixed(2)  : '—'}</td>
        <td style="color:${volColor};font-weight:${(c.volume??0)>100?'700':'400'};">${(c.volume??0).toLocaleString()}</td>
        <td style="color:${oiColor};">${(c.openInterest??0).toLocaleString()}</td>
        <td style="color:var(--warn);">${iv}</td>
        <td style="color:${isCall?'var(--green)':'var(--red)'};">${esc(delta)}</td>
        <td style="color:var(--text3);">${c.gamma != null ? c.gamma.toFixed(4) : '—'}</td>
        <td style="color:var(--red);">${c.theta  != null ? c.theta.toFixed(3)  : '—'}</td>
        <td>$${intrinsic}</td>
        <td>${badges}</td>
      </tr>`;
    }).join('');
  }

  private setView(view: 'both' | 'calls' | 'puts'): void {
    this.chainView = view;
    ['both','calls','puts'].forEach(v => {
      const btn = document.getElementById(`cv-${v}`);
      if (!btn) return;
      const active = v === view;
      btn.style.background  = active ? 'rgba(0,212,255,0.15)' : 'transparent';
      btn.style.borderColor = active ? 'rgba(0,212,255,0.4)'  : 'var(--border)';
      btn.style.color       = active ? 'var(--accent)'        : 'var(--text3)';
    });
    this.el('oc-calls-section').style.display = view === 'puts'  ? 'none' : 'block';
    this.el('oc-puts-section').style.display  = view === 'calls' ? 'none' : 'block';
  }

  private applyFilters(): void {
    const itmOnly  = (this.el('oc-itm-only') as HTMLInputElement).checked;
    const highOnly = (this.el('oc-high-vol') as HTMLInputElement).checked;
    document.querySelectorAll<HTMLElement>('.oc-row').forEach(row => {
      let show = true;
      if (itmOnly  && row.dataset.itm !== 'true')          show = false;
      if (highOnly && parseInt(row.dataset.vol ?? '0') <= 100) show = false;
      row.style.display = show ? '' : 'none';
    });
  }

  private showError(msg: string): void {
    const el = this.el('oc-error');
    el.style.display = 'block';
    el.textContent = msg;
  }
}
